import {MenuButton, MenuProvider} from '@ariakit/react'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {DotsHorizontalIcon, Download01Icon, EyeIcon, Key01Icon, Trash01Icon} from '@components/ui/icon/Icon.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {FC, useState} from 'react'
import {generatePath, Link} from 'react-router-dom'
import {usePasswordRecovery} from '@/features/auth/services/usePasswordRecovery.ts'
import {
    columnVisibilitySearchParamsToPayload,
    errorHandler,
    filtersParamToPayload,
    sortParamToPayload
} from '@utilities/helpers.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {useTheme} from 'styled-components'
import {DeleteUserModal} from '@/features/users/components/delete-user-modal/DeleteUserModal.tsx'
import {UserBase, UsersColumn} from '@/features/users/types.ts'
import {httpExportUser} from '@/features/users/http/users.http.ts'
import {useSendInvite} from '@/features/users/queries/useSendInvite.ts'
import {useUsersSearchParams} from '../../utils'
import {usePermissions} from '@/features/auth/store/store'
import {routes} from '@/utilities/constants/routes'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

export const RowMenu: FC<{
    user: UserBase & {roleName?: string | null}
    onDeleteSuccess?: () => void
    isDetails?: boolean
}> = ({user, onDeleteSuccess, isDetails}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const theme = useTheme()
    const {searchParams} = useUsersSearchParams()
    const [isOpen, setOpen] = useState(false)
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
    const passwordRecoveryMutation = usePasswordRecovery({
        onError: error => errorHandler(error),
        onSuccess: () => {
            toast.success(t('users:reset_password_success'))
            setOpen(false)
        }
    })
    const sendInviteMutation = useSendInvite({
        onError: error => errorHandler(error),
        onSuccess: () => {
            toast.success(t('users:send_invite_success'))
            setOpen(false)
        }
    })

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(UsersColumn, searchParams.columnVisibility) ??
        Object.fromEntries(UsersColumn.options.map(column => [column, true]))

    return (
        <>
            <MenuProvider open={isOpen} setOpen={setOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="200px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        {permissions?.userDetails?.read && (
                            <MenuItem
                                render={<Link to={generatePath(routes.USER_DETAILS.handle.path, {id: user.id})} />}
                            >
                                <EyeIcon />
                                {t('commons:details')}
                            </MenuItem>
                        )}
                        {permissions?.users?.write && (
                            <>
                                {!!user.roleName && !user.lastLoginAt ? (
                                    <MenuItem
                                        disabled={sendInviteMutation.isPending}
                                        onClick={event => {
                                            event.preventDefault()
                                            sendInviteMutation.mutate({userId: user.id})
                                        }}
                                    >
                                        <Key01Icon />
                                        {t('users:resend_invite')}
                                        {sendInviteMutation.isPending && <Spinner />}
                                    </MenuItem>
                                ) : user.roleName ? (
                                    <MenuItem
                                        disabled={passwordRecoveryMutation.isPending}
                                        onClick={event => {
                                            event.preventDefault()
                                            passwordRecoveryMutation.mutate({email: user.email})
                                        }}
                                    >
                                        <Key01Icon />
                                        {t('users:reset_password')}
                                        {passwordRecoveryMutation.isPending && <Spinner />}
                                    </MenuItem>
                                ) : (
                                    false
                                )}
                                <ExportModal
                                    entity="users"
                                    trigger={
                                        <MenuItem hideOnClick={false}>
                                            <Download01Icon />
                                            {t('commons:export_option')}
                                        </MenuItem>
                                    }
                                    hasViews={!isDetails}
                                    asyncExport
                                    fileName="Personale"
                                    promise={({extension, mode}) =>
                                        httpExportUser({
                                            payload: {
                                                selectedUserIds: [user.id],
                                                exceptedUserIds: [],
                                                type: extension,
                                                filters: filtersParamToPayload(searchParams.filters)?.filters,
                                                fields:
                                                    mode == 'view'
                                                        ? UsersColumn.exclude(['select', 'moreActions']).options.filter(
                                                              column => columnVisibility[column]
                                                          )
                                                        : UsersColumn.exclude(['select', 'moreActions']).options,
                                                sorts: sortParamToPayload(searchParams.sorts)
                                            }
                                        })
                                    }
                                    selectedCount={1}
                                    allowedExtensions={['xlsx', 'xls', 'csv']}
                                />
                            </>
                        )}
                    </MenuGroup>
                    {permissions?.users?.write && (
                        <MenuGroup>
                            <MenuItem
                                style={{color: theme.palette.danger['600']}}
                                onClick={() => setIsDeleteUserModalOpen(true)}
                            >
                                <Trash01Icon style={{fill: theme.palette.danger['600']}} />
                                {t('commons:delete')}
                            </MenuItem>
                        </MenuGroup>
                    )}
                </Menu>
            </MenuProvider>
            {isDeleteUserModalOpen && (
                <DeleteUserModal
                    user={user}
                    onClose={() => setIsDeleteUserModalOpen(false)}
                    onSuccess={onDeleteSuccess}
                />
            )}
        </>
    )
}
